import "./Style.css";
import { Link, Navigate } from "react-router-dom";
import Layout from "./Layout";
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { alertActions } from "../_actions";
import { history } from "../_helpers";
import axios from "axios";

// Dashboard components
import DashboardSkeleton from "./Components/DashboardSkeleton";
import LegalWorkflowStats from "./Components/LegalWorkflowStats";
import OperationalStatus from "./Components/OperationalStatus";
import WorkflowFunnel from "./Components/WorkflowFunnel";
import MonthlyFilingVolume from "./Components/MonthlyFilingVolume";
import PaymentStatusOverview from "./Components/PaymentStatusOverview";
import DepositAccountStats from "./Components/DepositAccountStats";

/**
 * Refactored Dashboard Component
 * - Uses reusable components instead of inline code
 * - Skeleton loading instead of full-page loader
 * - Cleaner, more maintainable structure
 */
export default function DashboardRefactore() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Workflow status counts
  const [workflowStats, setWorkflowStats] = useState({
    drafts: 0,           // Status 1 - Draft/Created
    pending: 0,          // Status 2 - Saved/Pending
    published: 0,        // Status 3 - Published
    awaitingPayment: 0,  // Unpaid announcements
  });

  const [dashData, setDashData] = useState({
    no_advertisement_publish: 0,
    no_pending_announcement: 0,
    no_invoices: 0,
    no_devis: 0,
    publish_advertise: [],
    forfaitaires: 0,
    modification: 0,
    balance_discount: 0,
    notifications_data: 0,
    payment_plan: null,
  });

  const currentUser = useSelector((state) => state.authentication.user);

  const getDashboardData = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}api/Dashboard/${currentUser?.data?.id}`
      );
      
      if (response.data.sucess === true) {
        const data = response.data.data;
        
        // Debug: Log API response structure
        console.log('Dashboard API Response:', data);
        
        // Extract data from API response
        // orderdevis = annonces with status=3 (published)
        // orderdossiers = annonces with status=2 (pending/awaiting)
        const orderdevis = data.orderdevis || [];
        const orderdossiers = data.orderdossiers || [];
        const orderdossiersPro = data.orderdossiersPro || [];
        const publish_advertise = data.publish_advertise || [];
        const userDetails = data.userDetails || {};
        const notifications_data = data.notifications_data || 0;
        
        // Calculate workflow stats - handle both .count and .length for compatibility
        const publishedCount = typeof orderdevis === 'object' && orderdevis.count !== undefined 
          ? orderdevis.count 
          : (Array.isArray(orderdevis) ? orderdevis.length : 0);
        
        const pendingCount = typeof orderdossiers === 'object' && orderdossiers.count !== undefined 
          ? orderdossiers.count 
          : (Array.isArray(orderdossiers) ? orderdossiers.length : 0);
        
        // Calculate awaiting payment (only if orderdossiers is an array)
        const awaitingPaymentCount = Array.isArray(orderdossiers) 
          ? orderdossiers.filter(item => 
              !item.payment_status || item.payment_status === 0 || item.payment_status === '0'
            ).length
          : 0;
        
        // Processing = pending items that have been paid
        const processingCount = pendingCount - awaitingPaymentCount;

        console.log('Workflow Stats:', { publishedCount, pendingCount, awaitingPaymentCount, processingCount });
        console.log('Monthly Data:', publish_advertise);

        setWorkflowStats({
          drafts: 0, // Status 1 - would need separate API call
          pending: processingCount > 0 ? processingCount : 0,
          published: publishedCount,
          awaitingPayment: awaitingPaymentCount > 0 ? awaitingPaymentCount : pendingCount,
        });

        const invoicesCount = typeof orderdossiersPro === 'object' && orderdossiersPro.count !== undefined
          ? orderdossiersPro.count
          : (Array.isArray(orderdossiersPro) ? orderdossiersPro.length : 0);

        setDashData({
          no_advertisement_publish: publishedCount,
          no_pending_announcement: pendingCount,
          no_devis: publishedCount,
          no_invoices: invoicesCount,
          publish_advertise: publish_advertise,
          forfaitaires: userDetails.forfaitaires || 0,
          modification: userDetails.modification || 0,
          balance_discount: userDetails.balance_discount || 0,
          notifications_data: notifications_data,
          payment_plan: userDetails.payment_plan || null,
        });

        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error("Dashboard data fetch error:", error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.data?.id]);

  useEffect(() => {
    history.listen(() => {
      dispatch(alertActions.clear());
    });
    if (currentUser?.data?.id) {
      getDashboardData();
    }
  }, [currentUser?.data?.id, dispatch, getDashboardData]);

  if (!currentUser || !currentUser?.data) {
    return <Navigate to="/" />;
  }

  return (
    <Layout>
      <div className="mb-8 col-span-12 xl:col-span-9">
        <div className="w-full max-w-full xl:max-w-[1400px] 2xl:max-w-[1600px] h-full bg-white rounded-[20px] overflow-hidden">
          
          {/* Profile Update Alert */}
          {(!currentUser?.data?.firstname || !currentUser?.data?.lastname) && (
            <div className="alert info mx-4 mt-4">
              Veuillez mettre à jour votre profil{" "}
              <Link to="/prouser/profile" className="font-semibold underline">
                ici
              </Link>
            </div>
          )}

          {/* Header */}
          <div className="mainHead min-h-[60px] sm:min-h-[70px] flex sm:flex-row flex-col sm:items-center p-3 sm:p-6 lg:p-9 sm:justify-between">
            <h1 className="text-base sm:text-lg md:text-xl font-extrabold text-white sm:mt-0 mt-4">
              Tableau de bord
            </h1>
          </div>

          {/* Operational Status Bar */}
          <OperationalStatus
            lastUpdated={lastUpdated}
            isLoading={isLoading}
            isError={isError}
            onRefresh={getDashboardData}
          />

          {isLoading ? (
            <DashboardSkeleton />
          ) : (
            <>
              {/* Legal Workflow Stats - Primary Focus */}
              <LegalWorkflowStats
                drafts={workflowStats.drafts}
                pending={workflowStats.pending}
                published={workflowStats.published}
                awaitingPayment={workflowStats.awaitingPayment}
                isLoading={isLoading}
                lastUpdated={lastUpdated}
              />

              {/* Analytics Grid - Responsive Layout */}
              <div className="mt-6 px-4 sm:px-6 lg:px-9">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
                  {/* Left Column - Workflow Funnel */}
                  <WorkflowFunnel
                    drafts={workflowStats.drafts}
                    pending={workflowStats.pending + workflowStats.awaitingPayment}
                    published={workflowStats.published}
                    isLoading={isLoading}
                  />

                  {/* Right Column - Monthly Volume */}
                  <MonthlyFilingVolume
                    data={dashData.publish_advertise}
                    isLoading={isLoading}
                  />
                </div>
              </div>

              {/* Payment & Discount Section */}
              <div className="mt-6 px-4 sm:px-6 lg:px-9">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
                  {/* Payment Status */}
                  <PaymentStatusOverview
                    pendingPayments={workflowStats.awaitingPayment}
                    monthlyPlanActive={dashData.payment_plan === 'monthly'}
                    totalPendingAmount={0}
                    invoicesDue={dashData.no_invoices}
                    isLoading={isLoading}
                  />

                  {/* Discount Section - Compact */}
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Vos remises</h5>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-600">Solde disponible</span>
                        <span className="text-lg font-bold text-primary">{dashData.balance_discount} €</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2 bg-gray-50 rounded">
                          <p className="text-xs text-gray-500">Remise forfaitaire</p>
                          <p className="text-sm font-semibold text-gray-700">{dashData.forfaitaires}%</p>
                        </div>
                        <div className="p-2 bg-gray-50 rounded">
                          <p className="text-xs text-gray-500">Remise modification</p>
                          <p className="text-sm font-semibold text-gray-700">{dashData.modification}%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Deposit Account Stats - Separate section */}
              <DepositAccountStats />

              {/* Quick Actions Footer */}
              <div className="mt-6 px-4 sm:px-6 lg:px-9 pb-6">
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <Link
                    to="/nos-formulairess"
                    className="inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Nouvelle annonce
                  </Link>
                  <Link
                    to="/prouser/annoncespublies"
                    className="inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Voir toutes les annonces
                  </Link>
                  <Link
                    to="/prouser/factures"
                    className="inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Mes factures
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
